import type { ProductImageEntry } from "./types";

const entry = (
  id: string,
  module: string,
  opens: string,
  setup: readonly string[],
  altSv: string,
  altEn: string,
  showsSv: string,
  showsEn: string,
  provesSv: string,
  provesEn: string,
): ProductImageEntry => ({
  id,
  module,
  area: "solution",
  opens,
  setup,
  scenario: "S1",
  visual: { variant: 0 },
  text: {
    sv: { alt: altSv, shows: showsSv, proves: provesSv },
    en: { alt: altEn, shows: showsEn, proves: provesEn },
  },
  use: ["pages/Plattform.tsx"],
});

const analysis = (section: string) =>
  `Schema → Analys → ${section} (sparat DEV-resultat)`;

export const ANALYTICS_MORE_DETAIL_CATALOGUE: readonly ProductImageEntry[] = [
  entry(
    "analytics.actions.repair-plan",
    "Åtgärder",
    analysis("Åtgärder"),
    ["Öppna det sparade DEV-resultatet i Analys", "Visa avsnittet Åtgärder"],
    "Första rekommenderade åtgärden för den valda lösningen",
    "First recommended action for the selected solution",
    "Åtgärdslistan visar ett rekommenderat steg med sin grupp och beskrivning.",
    "The action list shows a recommended step with its group and description.",
    "Steget visas i lösningens rekommendationslista.",
    "The step is shown in the solution's recommendation list.",
  ),
  entry(
    "analytics.advanced.constraint-score",
    "Poäng & villkorsanalys",
    analysis("Avancerat → Poäng & villkorsanalys"),
    [
      "Öppna det sparade DEV-resultatet i Analys",
      "Visa Poäng & villkorsanalys",
    ],
    "Hård, medel och mjuk poäng för den valda lösningen",
    "Hard, medium and soft scores for the selected solution",
    "Poängpanelen visar lösningens tre poängnivåer och villkorsanalys.",
    "The score panel shows the solution's three score levels and constraint analysis.",
    "Poängen och villkorsraderna kommer från lösningens analysdata.",
    "The scores and constraint rows come from the solution's analysis data.",
  ),
  entry(
    "analytics.advanced.ledger-balance",
    "Nyckeltal · tidsbalans",
    analysis("Avancerat → Nyckeltal"),
    ["Öppna det sparade DEV-resultatet i Analys", "Visa Nyckeltal"],
    "Exakt tidsbalans med indata, utdata och fördelade tidsdelar",
    "Exact time balance with input, output and time components",
    "Balansdiagrammet visar ledgerns in- och utdata samt tidsdelarna.",
    "The balance chart shows the ledger's input, output and time components.",
    "Diagrammet återger värden från lösningens exakta tidsledger.",
    "The chart renders values from the solution's exact time ledger.",
  ),
  entry(
    "analytics.advanced.finance-close",
    "Ekonomi · lösningsutfall",
    analysis("Avancerat → Ekonomi"),
    ["Öppna det sparade DEV-resultatet i Analys", "Visa Ekonomi"],
    "Ekonomikort med intäkt, kostnad och marginal för lösningen",
    "Finance card with revenue, cost and margin for the solution",
    "Kortet visar de ekonomifält som ledgern har för lösningen.",
    "The card shows the finance fields the ledger has for the solution.",
    "Fälten återger lösningens finansiella ledgerdata och eventuell prisinformation.",
    "The fields render the solution's financial ledger data and any rate information.",
  ),
  entry(
    "analytics.explore.inspector.employee",
    "Utforska · medarbetarinspektör",
    "Schema → Analys → Utforska → öppna en medarbetarrad",
    [
      "Öppna det sparade DEV-resultatet i Analys",
      "Öppna en medarbetarrad i Utforska",
    ],
    "Inspektör med mått för en vald medarbetarrad",
    "Inspector with measures for a selected employee row",
    "Inspektören visar måtten för den öppnade medarbetaren.",
    "The inspector shows measures for the opened employee.",
    "Sammanfattningen använder måtten för den valda medarbetarraden.",
    "The summary uses measures from the selected employee row.",
  ),
  entry(
    "analytics.explore.inspector.client",
    "Utforska · brukarinspektör",
    "Schema → Analys → Utforska → öppna en brukarrad",
    [
      "Öppna det sparade DEV-resultatet i Analys",
      "Öppna en brukarrad i Utforska",
    ],
    "Inspektör med mått för en vald brukarrad",
    "Inspector with measures for a selected client row",
    "Inspektören visar måtten för den öppnade brukaren.",
    "The inspector shows measures for the opened client.",
    "Sammanfattningen använder måtten för den valda brukarraden.",
    "The summary uses measures from the selected client row.",
  ),
  entry(
    "analytics.explore.table.employee-wait",
    "Utforska · väntetid per medarbetare",
    "Schema → Analys → Utforska → Medarbetare → Tabell → Väntetid",
    [
      "Öppna det sparade DEV-resultatet i Analys",
      "Visa väntetid per medarbetare i tabell",
    ],
    "Tabell med väntetidsmått per medarbetare",
    "Table of waiting-time measures by employee",
    "Tabellen visar väntetid för medarbetare och en summeringsrad.",
    "The table shows employee waiting time and a total row.",
    "Raderna visar Utforskas väntetidsmått för medarbetardimensionen.",
    "Rows show Explore's waiting-time measure for the employee dimension.",
  ),
  entry(
    "analytics.explore.table.client-continuity",
    "Utforska · kontinuitet per brukare",
    "Schema → Analys → Utforska → Brukare → Tabell → Kontinuitet",
    [
      "Öppna det sparade DEV-resultatet i Analys",
      "Visa kontinuitet per brukare i tabell",
    ],
    "Tabell med kontinuitetsmått per brukare",
    "Table of continuity measures by client",
    "Tabellen visar kontinuitetsmått för brukare och en summeringsrad.",
    "The table shows continuity measures for clients and a total row.",
    "Raderna visar Utforskas kontinuitetsmått för brukardimensionen.",
    "Rows show Explore's continuity measure for the client dimension.",
  ),
  entry(
    "analytics.explore.table.district-visits",
    "Utforska · besök per distrikt",
    "Schema → Analys → Utforska → Distrikt → Tabell → Besök",
    [
      "Öppna det sparade DEV-resultatet i Analys",
      "Visa antal besök per distrikt i tabell",
    ],
    "Tabell med besöksantal per distrikt",
    "Table of visit counts by district",
    "Tabellen visar besöksantal per distrikt och en summeringsrad.",
    "The table shows visit counts by district and a total row.",
    "Raderna visar Utforskas besöksmått för distriktsdimensionen.",
    "Rows show Explore's visit measure for the district dimension.",
  ),
  entry(
    "analytics.explore.weekday-demand-timeline",
    "Utforska · efterfrågan per veckodag",
    "Schema → Analys → Utforska → Veckodag → Tidslinje → Efterfrågan",
    [
      "Öppna det sparade DEV-resultatet i Analys",
      "Visa efterfrågan per veckodag i tidslinje",
    ],
    "Tidslinjediagram över efterfrågan per veckodag",
    "Timeline chart of demand by weekday",
    "Diagrammet visar efterfrågan för veckodagar i den valda perioden.",
    "The chart shows weekday demand in the selected period.",
    "Staplarna återger efterfrågemåttet från lösningens täckningsmatris.",
    "The bars render the demand measure from the solution's coverage matrix.",
  ),
];
